import React, { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { useQueryState } from '~/common/hooks'
import { useApp } from '~/common/hooks/useApp'
import DataTable from '~/components/DataTable'
import Icon from '~/components/Icon'
import IconButton from '~/components/IconButton'
import Dialog from '~/components/Dialog'
import Page from '~/components/Page'
import useGuestContact from '~/modules/shared/redux/hooks/useGuestContact'
import { convertFilterParams, convertSortParams } from '~/utils'

import { ROUTE } from '../routes/config'
import Status from '~/components/Status'
import { GUEST_CONTACT_STATUS_OPTIONS } from '~/modules/configuration/constants'
import TaskBar from '~/components/TaskBar'
import FilterArea from '~/components/FilterArea'
import ImportExport from '~/components/ImportExport'
import Tabs from '~/components/Tabs'
import FilterForm from './filter-form'
import { filterSchema } from './filter-form/schema'
import { exportGuestApi } from '~/modules/shared/redux/sagas/contact/search-guest-contact'

const breadcrumbs = [
  {
    route: ROUTE.GUEST.LIST.PATH,
    title: ROUTE.GUEST.LIST.TITLE,
  },
]

const GuestList = () => {
  const { t } = useTranslation(['mkt'])
  const [tempItem, setTempItem] = useState()
  const history = useHistory()
  const [selectedRows, setSelectedRows] = useState([])
  const { refreshKey, clearRefreshKey } = useApp()
  const [deleteModal, setDeleteModal] = useState(false)

  const DEFAULT_FILTERS = {
    email: '',
    name: '',
    countryId: null,
  }

  const {
    page,
    pageSize,
    sort,
    filters,
    keyword,
    tab,
    setPage,
    setPageSize,
    setFilters,
    setSort,
    setTab,
    withSearch,
    selectedRowsDeps,
  } = useQueryState({
    filters: DEFAULT_FILTERS,
    tab: '',
  })

  const tabList = [
    {
      label: t('common.all'),
      value: '',
    },
    {
      label: t('guestContactStatus.waitingForRegistration'),
      value: 1,
    },
    {
      label: t('guestContactStatus.confirmed'),
      value: 2,
    },
  ]


  const onSubmitDelete = () => {
    actions.deleteGuest(tempItem?.id, () => refreshData())
    setDeleteModal(false)
  }

  const {
    data: { isLoading, guestContactList = [], total },
    actions,
  } = useGuestContact()

  const columns = useMemo(
    () => [
      {
        field: 'fullName',
        headerName: t('guest.fullName'),
        visible: 'always',
        sortable: true,
        renderCell: (params) => {
          const { row } = params
          return row?.fullName
        },
      },
      {
        field: 'phone',
        headerName: t('guest.phoneNumber'),
        visible: 'always',
        sortable: true,
        renderCell: (params) => {
          const { row } = params
          return row?.phone
        },
      },
      {
        field: 'email',
        headerName: t('guest.workEmail'),
        visible: 'always',
        sortable: true,
        renderCell: (params) => {
          const { row } = params
          return row?.email
        },
      },
      // {
      //   field: 'companyName',
      //   headerName: t('guest.companyName'),
      //   visible: 'always',
      //   sortable: false,
      //   renderCell: (params) => {
      //     const { row } = params
      //     return row?.companyName
      //   },
      // },
      {
        field: 'addressZone',
        headerName: t('guest.area'),
        visible: 'always',
        sortable: false,
        renderCell: (params) => {
          const { row } = params
          return row?.addressZone
        },
      },
      // {
      //   field: 'role',
      //   headerName: t('guest.currentRole'),
      //   visible: 'always',
      //   sortable: false,
      //   renderCell: (params) => {
      //     const { row } = params
      //     const keys = row?.role.split(',')
      //     const values = keys?.map((key) => t(key))
      //     return values?.join(',')
      //   },
      // },
      // {
      //   field: 'bussinessType',
      //   headerName: t('guest.workType'),
      //   visible: 'always',
      //   sortable: false,
      //   renderCell: (params) => {
      //     const { row } = params
      //     const keys = row?.bussinessType.split(',')
      //     const values = keys?.map((key) => t(key))
      //     return values?.join(',')
      //   },
      // },
      // {
      //   field: 'features',
      //   headerName: t('guest.interestFeature'),
      //   visible: 'always',
      //   sortable: false,
      //   renderCell: (params) => {
      //     const { row } = params
      //     const keys = row?.features.split(',')
      //     const values = keys?.map((key) => t(key))
      //     return values?.join(',')
      //   },
      // },
      // {
      //   field: 'purpose',
      //   headerName: t('guest.experientalPurpose'),
      //   visible: 'always',
      //   sortable: false,
      //   renderCell: (params) => {
      //     const { row } = params
      //     const keys = row?.purpose.split(',')
      //     const values = keys?.map((key) => t(key))
      //     return values?.join(',')
      //   },
      // },
      {
        field: 'status',
        headerName: t('guest.status'),
        width: 200,
        sortable: true,
        align: 'center',
        visible: 'always',
        renderCell: (params) => {
          const { status } = params.row
          return (
            <Status options={GUEST_CONTACT_STATUS_OPTIONS} value={status} />
          )
        },
      },
      {
        field: 'action',
        headerName: t('guest.action'),
        width: 200,
        align: 'center',
        visible: 'always',
        sticky: 'right',
        growUp: false,
        renderCell: (params) => {
          const { id } = params.row
          return (
            <div>
              <IconButton
                onClick={() => {
                  setTempItem(params.row)
                  setDeleteModal(true)
                }}
              >
                <Icon name="delete" />
              </IconButton>
              <IconButton
                onClick={() =>
                  history.push(
                    withSearch(ROUTE.GUEST.DETAIL.PATH.replace(':id', `${id}`)),
                  )
                }
              >
                <Icon name="show" />
              </IconButton>
            </div>
          )
        },
      },
    ],
    [guestContactList],
  )

  const refreshData = () => {
    const params = {
      keyword: keyword.trim(),
      page: page,
      limit: pageSize,
      filter: convertFilterParams({ ...filters, status: tab }),
      sort: convertSortParams(sort),
    }
    actions.searchGuestContact(params)
  }

  useEffect(() => {
    refreshData()
  }, [keyword, page, pageSize, filters, sort, tab])

  useEffect(() => {
    if (refreshKey) {
      if (guestContactList?.some((item) => item?.id === refreshKey)) {
        refreshData()
      }

      clearRefreshKey()
    }
  }, [refreshKey, guestContactList])

  useEffect(() => {
    setSelectedRows([])
  }, [selectedRowsDeps])

  return (
    <Page
      breadcrumbs={breadcrumbs}
      title={t('menu.guest')}
      loading={isLoading}
      // onSearch={setKeyword}
      placeholder={t('roleManagement.searchPlaceholder')}
      onBack={() => history.goBack()}
    >
      <TaskBar>
        <ImportExport
          name={t('exportFileName.defineCustomer')}
          onExport={() =>
            exportGuestApi({
              filter: convertFilterParams({
                ...filters,
                status: tab,
              }),
              sort: convertSortParams(sort),
            })
          }
          onRefresh={refreshData}
        />
      </TaskBar>

      <FilterArea
        values={filters}
        onApply={setFilters}
        form={<FilterForm />}
        defaultValues={DEFAULT_FILTERS}
        validationSchema={filterSchema(t)}
      />
      <DataTable
        title={t('guest.list')}
        columns={columns}
        rows={guestContactList || []}
        pageSize={pageSize}
        page={page}
        onPageChange={setPage}
        hideSetting
        onPageSizeChange={setPageSize}
        onSortChange={setSort}
        onSelectionChange={setSelectedRows}
        selected={selectedRows}
        total={total}
        sort={sort}
        tabs={<Tabs list={tabList} value={tab} onChange={setTab} />}
      />
      <Dialog
        open={deleteModal}
        title='Bạn có muốn xoá khách ?'
        onCancel={() => setDeleteModal(false)}
        cancelLabel='Huỷ'
        onSubmit={onSubmitDelete}
        submitLabel='Xoá'
        submitProps={{
          color: 'error',
        }}
        noBorderBottom
      >
      </Dialog>
    </Page>
  )
}

export default GuestList
